import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api, { formatDate } from "@/lib/api";
import SEO from "@/components/SEO";
import JournalImage, { isJournalBrandImage } from "@/components/JournalImage";
import { FadeUp } from "@/components/Motion";

function trackJournalLink(href) {
  if (!window.gtag) return;

  const destination =
    href.startsWith("/contact")
      ? "contact"
      : href.startsWith("/service")
        ? "service"
        : href.startsWith("/")
          ? "internal"
          : "external";

  window.gtag("event", "journal_link_click", {
    destination,
    link_url: href,
    interaction_location: window.location.pathname,
  });
}

function renderInline(text) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }

    const match = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);

    if (!match) return part;

    const [, label, href] = match;
    if (!/^(https?:\/\/|\/(?!\/))/.test(href)) return label;
    const isInternal = href.startsWith("/");

    if (isInternal) {
      return (
        <Link
          key={index}
          to={href}
          onClick={() => trackJournalLink(href)}
          className="font-semibold text-orange underline underline-offset-4"
        >
          {label}
        </Link>
      );
    }

    return (
      <a
        key={index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackJournalLink(href)}
        className="font-semibold text-orange underline underline-offset-4"
      >
        {label}
      </a>
    );
  });
}

function ArticleBlock({ block, index }) {
  const value = String(block || "").trim();

  if (!value) return null;

  const image = value.match(/^!\[([^\]]*)\]\(([^)]+)\)(?:\n([\s\S]+))?$/);
  if (image) {
    const [, alt, src, caption] = image;
    if (!/^(https:\/\/|\/(?!\/))/.test(src)) return null;
    return (
      <FadeUp>
        <figure className="my-10">
          <img src={src} alt={alt} loading="lazy" decoding="async"
            className="mx-auto max-h-[80vh] w-auto max-w-full rounded-2xl border border-border" />
          {caption && <figcaption className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {renderInline(caption)}
          </figcaption>}
        </figure>
      </FadeUp>
    );
  }

  if (value.startsWith("### ")) {
    return (
      <FadeUp delay={Math.min(index * 0.05, 0.2)}>
        <h3 className="mt-10 font-display text-xl font-bold tracking-tight sm:text-2xl">
          {renderInline(value.slice(4))}
        </h3>
      </FadeUp>
    );
  }

  if (value.startsWith("## ")) {
    return (
      <FadeUp delay={Math.min(index * 0.05, 0.2)}>
        <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
          {renderInline(value.slice(3))}
        </h2>
      </FadeUp>
    );
  }

  const lines = value.split("\n").map((line) => line.trim()).filter(Boolean);
  const isList = lines.length > 0 && lines.every((line) => line.startsWith("- "));

  if (isList) {
    return (
      <FadeUp delay={Math.min(index * 0.05, 0.2)}>
        <ul className="mt-6 space-y-3 pl-5 text-base leading-relaxed sm:text-lg">
          {lines.map((line, itemIndex) => (
            <li key={itemIndex} className="list-disc pl-1">
              {renderInline(line.slice(2))}
            </li>
          ))}
        </ul>
      </FadeUp>
    );
  }

  return (
    <FadeUp delay={Math.min(index * 0.05, 0.2)}>
      <p
        className={`text-base leading-loose sm:text-lg ${index > 0 ? "mt-8" : ""} ${
          index === 0
            ? "first-letter:float-left first-letter:mr-3 first-letter:font-display first-letter:text-6xl first-letter:font-extrabold first-letter:leading-[0.85] first-letter:text-orange"
            : ""
        }`}
      >
        {renderInline(value)}
      </p>
    </FadeUp>
  );
}

function caseStudySections(content = []) {
  const sections = [];
  for (const block of content) {
    if (block.startsWith('## ')) sections.push({ title: block.slice(3), blocks: [] });
    else if (sections.length) sections[sections.length - 1].blocks.push(block);
  }
  return sections;
}

function CaseStudyLayout({ post }) {
  const sections = caseStudySections(post.content);
  const find = (title) => sections.find((section) => section.title === title);
  const facts = find('Quick facts');
  const watch = sections.find((section) => section.title.startsWith('Watch'));
  const cta = find('CTA');
  const hasImage = !isJournalBrandImage(post.image);
  const title = post.title.split(' | ')[0];
  const button = (block, index, primary = false) => {
    const match = block.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!match || !/^(https?:\/\/|\/(?!\/))/.test(match[2])) return null;
    const [, label, href] = match;
    const className = `inline-flex min-h-12 items-center justify-center gap-3 rounded-full px-6 py-3 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-orange ${primary ? 'bg-orange text-ink hover:bg-orange/85' : 'border border-border hover:border-orange hover:text-orange'}`;
    return href.startsWith('/') ? <Link key={index} to={href} className={className} onClick={() => trackJournalLink(href)}>{label.replace(/\.$/, '')}<span aria-hidden="true">↗</span></Link> : <a key={index} href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={() => trackJournalLink(href)}>{label.replace(/\.$/, '')}<span aria-hidden="true">↗</span></a>;
  };
  const details = (blocks) => <dl className="grid gap-x-8 gap-y-7 sm:grid-cols-2 lg:grid-cols-3">{blocks.map((block, i) => {
    const match = block.match(/^\*\*([^*]+)\*\*\s*\n([\s\S]+)$/);
    return match ? <div key={i}><dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{match[1]}</dt><dd className="mt-2 text-base font-medium leading-relaxed">{renderInline(match[2])}</dd></div> : null;
  })}</dl>;
  const body = (blocks) => blocks.map((block, i) => {
    if (block.startsWith('### ')) return <h3 key={i} className="pt-5 font-display text-xl font-bold tracking-tight sm:text-2xl">{renderInline(block.slice(4))}</h3>;
    const lines = block.split('\n').filter(Boolean);
    if (lines.every((line) => line.startsWith('- '))) return <ul key={i} className="grid gap-3 sm:grid-cols-2">{lines.map((line, n) => <li key={n} className="flex gap-3 text-base leading-relaxed"><span aria-hidden="true" className="text-orange">↗</span><span>{renderInline(line.slice(2))}</span></li>)}</ul>;
    return <p key={i} className="text-base leading-relaxed text-muted-foreground sm:text-lg">{renderInline(block)}</p>;
  });
  return <div data-testid="case-study-layout">
    <header className="mx-auto max-w-7xl px-4 pb-12 pt-12 sm:px-6 lg:px-8 lg:pb-16 lg:pt-20">
      <Link to="/journal" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-orange"><ArrowLeft size={14} /> Journal</Link>
      <div className="mt-10 grid gap-8 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7"><p className="font-mono text-xs uppercase tracking-[0.2em] text-orange">Case study · Incolour Films</p><h1 data-testid="article-title" className="mt-5 font-display text-4xl font-extrabold leading-[1.0] tracking-tight sm:text-6xl lg:text-7xl">{title}</h1><p className="mt-6 max-w-2xl font-serif text-xl italic leading-relaxed sm:text-2xl">{post.content?.[0]}</p></div>
        <div className="lg:col-span-5"><p className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">{post.excerpt}</p>{watch && <div className="mt-7 flex flex-wrap gap-3">{watch.blocks.slice(0, 1).map((block, i) => button(block, i, true))}</div>}</div>
      </div>
    </header>
    {hasImage && <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8 lg:pb-16"><img src={post.image} alt={`${title} — film still`} fetchPriority="high" className="aspect-video w-full rounded-2xl border border-border object-cover" /></div>}
    {facts && <section aria-label="Production facts" className="border-y border-border"><div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">{details(facts.blocks)}</div></section>}
    <div data-testid="article-body">
      {sections.filter((section) => !['Quick facts', 'CTA'].includes(section.title)).map((section, index) => <section key={section.title} className="border-b border-border py-12 lg:py-16"><div className="mx-auto grid max-w-7xl gap-7 px-4 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8"><div className="lg:col-span-4"><p className="mb-3 font-mono text-xs tracking-[0.2em] text-orange">{String(index + 1).padStart(2, '0')}</p><h2 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{section.title}</h2></div><div className="min-w-0 lg:col-span-8">
        {section.title === 'Key credits' ? details(section.blocks) : section.title.startsWith('Watch') ? <div className="flex flex-wrap gap-3">{section.blocks.map((block, i) => button(block, i, i === 0))}</div> : ['Behind the scenes', 'Film stills'].includes(section.title) ? <div className="columns-1 gap-6 sm:columns-2">{section.blocks.map((block, i) => {
          const match = block.match(/^!\[([^\]]*)\]\(([^)]+)\)(?:\n([\s\S]+))?$/);
          if (!match || !/^(https:\/\/|\/(?!\/))/.test(match[2])) return null;
          return <figure key={i} className="mb-7 break-inside-avoid"><img src={match[2]} alt={match[1]} loading="lazy" decoding="async" className="h-auto w-full rounded-2xl border border-border" /><figcaption className="mt-3 text-xs leading-relaxed text-muted-foreground">{renderInline(match[3])}</figcaption></figure>;
        })}</div> : <div className="max-w-3xl space-y-5">{body(section.blocks)}</div>}
      </div></div></section>)}
    </div>
    {cta && <section className="mx-auto grid max-w-7xl gap-7 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8 lg:py-24"><div><p className="font-mono text-xs uppercase tracking-[0.2em] text-orange">Your next production</p><h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{cta.blocks.find((block) => !block.startsWith('['))}</h2></div><div className="flex flex-wrap gap-3 lg:justify-end">{cta.blocks.filter((block) => block.startsWith('[')).map((block, i, all) => button(block, i, i === all.length - 1))}</div></section>}
    <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8"><Link to="/journal" className="inline-flex items-center gap-2 text-sm font-semibold text-orange"><ArrowLeft size={16} /> More from the journal</Link></div>
  </div>;
}

export default function Article() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);


  useEffect(() => {
    setPost(null);
    setNotFound(false);

    api
      .get(`/journal/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div
        className="flex min-h-[70vh] flex-col items-center justify-center gap-6 pt-16"
        data-testid="article-not-found"
      >
        <SEO title="Article Not Found" noindex />
        <h1 className="font-display text-3xl font-bold">Article not found</h1>

        <Link
          to="/journal"
          className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream dark:bg-cream dark:text-ink"
        >
          Back to journal
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div
        className="flex min-h-[70vh] items-center justify-center pt-16 text-muted-foreground"
        data-testid="article-loading"
      >
        Loading…
      </div>
    );
  }

  const seoDescription = (
    post.excerpt ||
    post.content?.[0] ||
    "Read the latest production stories, case studies and filmmaking insights from Incolour Films."
  ).slice(0, 160);

  return (
    <article
      className="pt-16"
      data-testid={`article-page-${post.slug}`}
    >
      <SEO
        title={post.title}
        description={seoDescription}
        image={post.image}
        path={`/journal/${post.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: post.title,
              description: seoDescription,
              image: post.image,
              datePublished: post.published_at,
              dateModified: post.updated_at || post.published_at,
              author: {
                "@type":
                  !post.author || post.author === "Incolour Films"
                    ? "Organization"
                    : "Person",
                name: post.author || "Incolour Films",
              },
              publisher: {
                "@type": "Organization",
                name: "Incolour Films",
                url: "https://incolourfilms.ca",
                logo: {
                  "@type": "ImageObject",
                  url: "https://incolourfilms.ca/logo-mark.webp",
                },
              },
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `https://incolourfilms.ca/journal/${post.slug}`,
              },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Journal",
                  item: "https://incolourfilms.ca/journal",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: post.title,
                  item: `https://incolourfilms.ca/journal/${post.slug}`,
                },
              ],
            },
          ],
        }}
      />

      {post.category === "Case Studies" ? <CaseStudyLayout post={post} /> : <>
      <header className="mx-auto max-w-3xl px-4 pt-16 sm:px-6 lg:pt-24">
        <Link
          to="/journal"
          data-testid="article-back-link"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-orange"
        >
          <ArrowLeft size={14} /> Journal
        </Link>

        <p className="mt-8 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
          <span className="text-orange">{post.category}</span> —{" "}
          {formatDate(post.published_at)} — By {post.author}
        </p>

        <h1
          className="mt-5 font-display text-3xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl"
          data-testid="article-title"
        >
          {post.title}
        </h1>

        {post.excerpt && (
          <p className="mt-6 font-serif text-xl italic leading-relaxed text-muted-foreground">
            {post.excerpt}
          </p>
        )}
      </header>

      {(
        <FadeUp className="mx-auto mt-12 max-w-5xl px-4 sm:px-6">
          <div className="overflow-hidden rounded-2xl border border-border">
            <JournalImage
              src={post.image}
              alt={post.title}
              className="aspect-[16/8] w-full object-cover"
            />
          </div>
        </FadeUp>
      )}

      <div
        className="mx-auto max-w-3xl px-4 py-16 sm:px-6"
        data-testid="article-body"
      >
        {post.content.map((block, i) => (
          <ArticleBlock key={i} block={block} index={i} />
        ))}

        <div className="mt-16 border-t border-border pt-8">
          <Link
            to="/journal"
            className="text-sm font-semibold text-orange"
            data-testid="article-more-link"
          >
            ← More from the journal
          </Link>
        </div>
      </div>
      </>}
    </article>
  );
}


